import React from 'react';
import { 
  CheckCircle, 
  AlertTriangle, 
  User, 
  Calendar, 
  Package,
  ClipboardCheck,
  Camera,
  ShoppingCart
} from 'lucide-react';

export interface TimelineEvent {
  id: number | string;
  type: 'inspection' | 'assignment' | 'created' | 'order' | 'status_change';
  date: string;
  title: string;
  description: string;
  status?: 'PASS' | 'FAIL' | 'NEEDS_REPAIR' | 'CALIBRATED';
  user?: {
    firstName: string;
    lastName: string;
  };
  severity?: string;
  photoUrls?: string[];
}

interface AssetHistoryTimelineProps {
  assetId: number;
  assetName: string;
  events: TimelineEvent[];
}

export function AssetHistoryTimeline({ assetName, events }: AssetHistoryTimelineProps) {
  const getEventIcon = (type: string, status?: string) => {
    switch (type) {
      case 'inspection':
        if (status === 'PASS' || status === 'CALIBRATED') {
          return <CheckCircle className="h-5 w-5 text-green-600" />;
        }
        if (status === 'FAIL') {
          return <AlertTriangle className="h-5 w-5 text-red-600" />;
        }
        return <ClipboardCheck className="h-5 w-5 text-yellow-600" />;
      case 'assignment':
        return <User className="h-5 w-5 text-blue-600" />;
      case 'order':
        return <ShoppingCart className="h-5 w-5 text-green-600" />;
      case 'created':
        return <Package className="h-5 w-5 text-gray-600" />;
      case 'status_change':
        return <Calendar className="h-5 w-5 text-purple-600" />;
      default:
        return <Calendar className="h-5 w-5 text-gray-600" />;
    }
  };

  const getEventColor = (type: string, status?: string) => {
    switch (type) {
      case 'inspection':
        if (status === 'PASS' || status === 'CALIBRATED') return 'border-green-500 bg-green-50';
        if (status === 'FAIL') return 'border-red-500 bg-red-50';
        return 'border-yellow-500 bg-yellow-50';
      case 'assignment':
        return 'border-blue-500 bg-blue-50';
      case 'order':
        return 'border-green-500 bg-green-50';
      case 'created':
        return 'border-gray-500 bg-gray-50';
      case 'status_change':
        return 'border-purple-500 bg-purple-50';
      default:
        return 'border-gray-500 bg-gray-50';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-gray-900">Asset History Timeline</h3>
        <span className="text-sm text-gray-500">{events.length} events</span>
      </div>

      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {/* Events */}
        <div className="space-y-6">
          {events.map((event, index) => (
            <div key={event.id} className="relative flex gap-4">
              {/* Icon */}
              <div className="relative z-10 flex items-center justify-center w-12 h-12 bg-white rounded-full border-2 border-gray-200">
                {getEventIcon(event.type, event.status)}
              </div>

              {/* Content */}
              <div className={`flex-1 p-4 rounded-lg border-2 ${getEventColor(event.type, event.status)}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{event.title}</h4>
                    <p className="text-sm text-gray-600">{formatDate(event.date)}</p>
                  </div>
                  {event.status && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      event.status === 'PASS' || event.status === 'CALIBRATED'
                        ? 'bg-green-100 text-green-800'
                        : event.status === 'FAIL'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {event.status.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>

                <p className="text-gray-700 mb-3">{event.description}</p>

                {event.user && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <User className="h-4 w-4" />
                    <span>{event.user.firstName} {event.user.lastName}</span>
                  </div>
                )}

                {event.severity && (
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${
                    event.severity === 'critical'
                      ? 'bg-red-100 text-red-800'
                      : event.severity === 'major'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    <AlertTriangle className="h-3 w-3" />
                    {event.severity.toUpperCase()} Severity
                  </div>
                )}

                {event.photoUrls && event.photoUrls.length > 0 && (
                  <div className="mt-3 flex items-center gap-2">
                    <Camera className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {event.photoUrls.length} photo{event.photoUrls.length > 1 ? 's' : ''} attached
                    </span>
                  </div>
                )}

                {/* First/Last event indicator */}
                {index === 0 && (
                  <div className="mt-3 text-xs text-gray-500 font-medium">
                    Most Recent
                  </div>
                )}
                {event.type === 'created' && (
                  <div className="mt-3 text-xs text-gray-500 font-medium">
                    Asset Created
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {events.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-gray-600 font-medium">No history available</p>
          <p className="text-sm text-gray-500 mt-1">Events will appear here as they occur</p>
        </div>
      )}
    </div>
  );
}

